// Standalone trigger definitions that fire outbound agents — the source of
// truth for all triggers (Lead/Collections seeded). Referenced by AgentTrigger.
import { WEBHOOK_TRIGGER_CATALOG } from '../webhooks/trigger-catalog.js';
export const TRIGGER_SOURCE_VALUES = [
    'WEBHOOK',
    'INTEGRATION',
    'CRON',
];
/**
 * Per-variable type. Drives ingestion validation, agent-prompt formatting, and
 * downstream push-action coercion. `text` is the back-compat default — any
 * variable without an explicit type is treated as text.
 *
 * Order is the canonical UI order: contact-channel types (phone/email) first,
 * then text and primitives, then specialized.
 */
export const TRIGGER_VARIABLE_TYPE_VALUES = [
    'phone',
    'email',
    'text',
    'number',
    'date',
    'boolean',
    'url',
];
/**
 * Read-time normalizer. Existing trigger rows have variables without `type` and
 * carry `phoneVariable` / `emailVariable` as separate top-level pointers. This
 * function makes the shape canonical:
 *  - infer `type: 'phone'` for the variable named in `phoneVariable`
 *  - infer `type: 'email'` for the variable named in `emailVariable`
 *  - default everything else to `text`
 *
 * Safe to call on already-normalized triggers (idempotent).
 */
export function normalizeTriggerVariables(trigger) {
    return {
        ...trigger,
        variables: trigger.variables.map(v => ({
            ...v,
            type: v.type ??
                (v.name === trigger.phoneVariable
                    ? 'phone'
                    : v.name === trigger.emailVariable
                        ? 'email'
                        : 'text'),
        })),
    };
}
/**
 * Derive the legacy `phoneVariable` / `emailVariable` pointers from the new
 * typed variables. Used at write time so old consumers keep working while the
 * UI is updated.
 */
export function deriveContactVariablesFromTypes(variables) {
    return {
        phoneVariable: variables.find(v => v.type === 'phone')?.name ?? null,
        emailVariable: variables.find(v => v.type === 'email')?.name ?? null,
    };
}
/** Slugify a trigger name into a wire event name (e.g. "Pest Control" -> "pest.control"). */
export function triggerNameToEventName(name) {
    return name
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '.')
        .replace(/^\.+|\.+$/g, '');
}
/** Lowercase alphanumeric segments separated by `.`, `_`, or `-`. */
export const TRIGGER_EVENT_NAME_PATTERN = /^[a-z0-9]+([._-][a-z0-9]+)*$/;
// Locked built-ins; derived from the catalog so new ones are added in one place.
export const BUILT_IN_TRIGGER_EVENT_NAMES = WEBHOOK_TRIGGER_CATALOG.map(entry => entry.eventName);
export function isBuiltInTriggerEvent(eventName) {
    return BUILT_IN_TRIGGER_EVENT_NAMES.includes(eventName);
}
export function isValidTriggerEventName(eventName) {
    return TRIGGER_EVENT_NAME_PATTERN.test(eventName);
}
//# sourceMappingURL=trigger.js.map